/**
 * Posting a ticket comment with any number of attached files: create the
 * comment, then upload each file against it (subject_type "ticket_comment",
 * subject_id = the new comment's id — see workflow_module's
 * add_ticket_comment.TICKET_COMMENT_ATTACHMENT_SUBJECT_TYPE).
 *
 * Every step has a time limit. A request that never answers (seen during a
 * blue/green deploy) otherwise leaves the composer stuck on "Posting..."
 * indefinitely with the user's draft already gone.
 */

import type { DataAdapter } from "../data/adapter";
import type * as API from "../data/api-types";

export const COMMENT_POST_TIMEOUT_MS = 30_000;
export const ATTACHMENT_UPLOAD_TIMEOUT_MS = 120_000;

export class TicketCommentTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TicketCommentTimeoutError";
  }
}

export interface PostTicketCommentInput {
  ticketId: string;
  customerId?: string;
  body: string;
  files: File[];
}

export interface PostTicketCommentResult {
  comment: API.TicketActivity;
  attachments: API.WorkflowAttachment[];
  /** Files that did not attach. The comment itself was posted, so the
   * composer keeps only these for the user to retry. */
  failedFiles: File[];
}

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  message: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new TicketCommentTimeoutError(message)),
      ms,
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/** Throws when the comment itself could not be posted (nothing was saved,
 * or — on a timeout — it may have saved late). Upload failures do not throw;
 * they come back in `failedFiles`. */
export async function postTicketComment(
  dataAdapter: Pick<
    DataAdapter,
    "addTicketComment" | "uploadWorkflowAttachment"
  >,
  { ticketId, customerId, body, files }: PostTicketCommentInput,
): Promise<PostTicketCommentResult> {
  const comment = await withTimeout(
    dataAdapter.addTicketComment(ticketId, body, files.length > 0),
    COMMENT_POST_TIMEOUT_MS,
    "Posting took too long. Refresh the page to check whether your comment saved before posting it again.",
  );

  const uploads = await Promise.allSettled(
    files.map((file) =>
      withTimeout(
        dataAdapter.uploadWorkflowAttachment({
          subjectType: "ticket_comment",
          subjectId: comment.id,
          file,
          customerId,
        }),
        ATTACHMENT_UPLOAD_TIMEOUT_MS,
        `Uploading ${file.name} took too long.`,
      ),
    ),
  );

  const attachments: API.WorkflowAttachment[] = [];
  const failedFiles: File[] = [];
  uploads.forEach((upload, index) => {
    if (upload.status === "fulfilled") attachments.push(upload.value);
    else failedFiles.push(files[index]!);
  });
  return { comment, attachments, failedFiles };
}
